import { Language } from "api/Api";
import dayjs from "dayjs";

export enum Type {
    Hygienist = 0,
    Assistant = 1,
    Admin = 2,
}

export const HYGIENIST_TYPES = {
    HYGIENIST: 0,
    ASSISTANT: 1,
    FRONT_DESK: 2,
    ASSOCIATE_DENTIST: 3
} as const;

export const COUNTRIES = {
    USA: 1,
    CA: 0,
} as const;

export type MembershipMode = 'shadow' | 'enforced';
export type TempShiftAccess = 'full' | 'restricted' | 'protected' | 'course_guidance';

export interface MembershipRestrictions {
    maxDistanceKm: number | null;
    maxLeadTimeDays: number | null;
    maxBookedShifts: number | null;
    effectiveMaxBookedShifts: number | null;
}

export interface MembershipGrandfathering {
    active: boolean;
    until: string | null;
    group: 'activity-only' | 'score-only' | 'both' | null;
    protectedStatusCode?: string | null;
    calculatedStatusCode?: string | null;
    windowDays?: number | null;
}

export interface MembershipActivity {
    state: 'building_history' | 'meets_requirement' | 'needs_recent_shift';
    completedShifts: number;
    ratedShifts: number;
    recentCompletedShifts: number;
    provisionalThreshold: number;
    maintenanceWindowDays: number;
    requiredShiftsPerWindow: number;
}

export interface MembershipNextAction {
    type: 'complete_shifts' | 'maintain_activity' | 'review_restrictions' | 'contact_support' | 'keep_going';
    title: string;
    description: string;
    ctaLabel: string;
    ctaPath: string;
}

export interface MembershipRecommendedCourse {
    id: number;
    code: string;
    title: string;
    url: string | null;
    /**
     * The matching `LmsCourses.lmsCourseId` when the membership course code
     * corresponds to a TalentLMS course. Null when no LMS course is paired
     * (e.g. the LMS catalog hasn't been seeded to match the recovery course
     * yet). Courses page uses this to visually highlight the assigned row
     * and to disable unrelated "Start Course" buttons while
     * `overlay.state === 'course_required'`.
     */
    lmsCourseId: number | null;
}

export interface CourseListItem {
    lmsCourseId: number;
    title: string;
    description: string | null;
    progressPercent: number | null;
    isCompleted: number;
    passedFinalQuiz: number;
    quizRequired?: number;
    isAssignedRecoveryCourse?: boolean;
}

/**
 * Membership course-recovery overlay. Populated by the API's
 * `GET /v3/hygienists/me/membership-status` endpoint when a recovery engine
 * has an active recovery row for this hygienist. Non-null in both `shadow`
 * and `live` modes; `enforced` marks whether the owning recovery engine is live.
 *
 * Two source systems can emit the same-shaped overlay:
 *
 *   - `valueGives` — provisional lane (`<5` completed shifts), backed by
 *     `tempstars-api/src/services/valueGives.service.ts`.
 *   - `ffs`        — established FFS Phase 2 lane (`5+` completed shifts),
 *     backed by `tempstars-api/src/services/ffsRecovery.service.ts`.
 *
 * The visible UX is identical for both lanes (the state machine exposes the
 * same `course_required` / `restricted_reentry` / `not_a_fit` experience).
 * `source` exists for analytics and for any future source-specific
 * admin-side detail pages.
 */
export type MembershipOverlayState = 'course_required' | 'restricted_reentry' | 'on_a_break' | 'not_a_fit' | 'duplicate_review';

export type MembershipOverlaySource = 'valueGives' | 'ffs' | 'support_manual' | 'duplicate';

export type MembershipReasonCategory =
    | 'credential_review'
    | 'administrative_block'
    | 'legacy_import'
    | 'system_outcome'
    | 'unknown';

export type MembershipOverlayMechanism =
    | 'overlay_restriction'
    | 'target_floor'
    | 'fixed_offset'
    | null;

export interface MembershipOverlay {
    active: boolean;
    enforced?: boolean;
    source: MembershipOverlaySource;
    state: MembershipOverlayState;
    profile: string | null;
    mechanism?: MembershipOverlayMechanism;
    /** Member-safe classification. Private notes, evidence, and actor data are never serialized. */
    reasonCategory?: MembershipReasonCategory;
    restrictions: MembershipRestrictions | null;
    activatedOn: string | null;
    reentryStartedOn: string | null;
    onABreakUntil?: string | null;
    floorApplied?: {
        reliability: boolean;
        feedback: boolean;
    };
    /**
     * Only set for `duplicate_review`: masked email of the matched existing
     * account (e.g. `k•••@g•••.com`) for the "already have an account?"
     * nudge. Never the raw address.
     */
    maskedEmail?: string | null;
}

export type MembershipStatusCategory =
    | 'top_tier' | 'active' | 'restricted'
    | 'provisional' | 'protected' | 'course_guidance'
    | 'provisional_course_required' | 'provisional_restricted_reentry' | 'provisional_on_a_break' | 'provisional_not_a_fit'
    | 'ffs_recovery_course_required' | 'ffs_recovery_restricted_reentry' | 'ffs_recovery_on_a_break' | 'ffs_recovery_not_a_fit'
    | 'duplicate_review';

/**
 * Grandfathering retention guidance (T2-6060). Generated entirely by the API
 * for Talent under active rollout protection: the app renders the compact
 * guidance section and detail sheets without recalculating FFS business
 * logic. Absent (undefined) for promoted, unchanged, expired, or otherwise
 * unprotected Talent.
 */
export type RetentionInsightConfidence =
    | 'exact'
    | 'projected'
    | 'depends_on_office_feedback'
    | 'on_track'
    | 'insufficient_history'
    | 'fallback';

export type RetentionInsightSource =
    | 'activity_only'
    | 'reliability_gap'
    | 'feedback_gap'
    | 'completed_shift_count_gap'
    | 'rated_shift_count_gap'
    | 'course_fallback'
    | 'current_status_supported'
    | 'build_history'
    | 'insufficient_history'
    | 'data_refresh_pending';

export type RetentionInsightKey =
    | 'reliability'
    | 'feedback'
    | 'activity'
    | 'completed_shift_count'
    | 'rated_shift_count'
    | 'course_fallback'
    | 'current_status'
    | 'data_refresh';

export interface RetentionInsightItem {
    key: RetentionInsightKey;
    source: RetentionInsightSource;
    title: string;
    summary: string;
    detailTitle: string;
    detailBody: string;
    confidence: RetentionInsightConfidence;
    icon: 'check' | 'star' | 'calendar' | 'course' | 'building' | 'alert' | 'shield';
    priority: number;
    isPrimaryBlocker: boolean;
    ctaLabel?: string | null;
    ctaPath?: string | null;
}

export interface RetentionInsights {
    visible: boolean;
    audience: 'grandfathered';
    heading: string;
    subheading: string;
    currentLabel: string;
    projectedLabel: string | null;
    protectionUntil: string | null;
    daysRemaining: number | null;
    protectionWindowDays: number | null;
    primaryAction: RetentionInsightItem | null;
    supportingItems: RetentionInsightItem[];
    footerNote?: string | null;
}

export interface MembershipStatusResponse {
    mode: MembershipMode;
    currentStatusCode: string | null;
    displayLabel: string;
    statusCategory: MembershipStatusCategory;
    reliabilityLabel: string | null;
    feedbackLabel: string | null;
    completedShifts: number;
    ratedShifts: number;
    activity: MembershipActivity;
    restrictions: MembershipRestrictions | null;
    grandfathering: MembershipGrandfathering;
    tempShiftAccess: TempShiftAccess;
    nextAction: MembershipNextAction;
    recommendedCourse: MembershipRecommendedCourse | null;
    recommendedCourses?: MembershipRecommendedCourse[];
    overlay: MembershipOverlay | null;
    retentionInsights?: RetentionInsights;
}

export type MembershipRestrictionErrorCode =
    | 'DISTANCE_EXCEEDED' | 'LEAD_TIME_EXCEEDED' | 'MAX_BOOKED_REACHED'
    | 'COURSE_REQUIRED' | 'ON_A_BREAK' | 'NOT_A_FIT' | 'CANCELLATION_COOLDOWN' | 'DUPLICATE_REVIEW';

export type MembershipRestrictionErrorType =
    | 'distance' | 'lead_time' | 'max_booked' | 'course_required' | 'on_a_break' | 'not_a_fit' | 'cancellation_cooldown' | 'duplicate_review';

export interface MembershipRestrictionErrorData {
    type: 'membership_restriction';
    code?: MembershipRestrictionErrorCode | string;
    restrictionType?: MembershipRestrictionErrorType | string;
    details?: Record<string, unknown> | null;
    membership?: MembershipStatusResponse | null;
    ctaLabel?: string;
    ctaPath?: string;
}

export interface SkillsProps {
    id: number
    hygienistId: number
    skillId: number
    hygienistType: number
    label: string
    name: string
    description: string
    certified: string
    qualification: number
    license: number
}

export interface PartialOffer {
    id: number
    jobId: number
    hygienistId: number
    offeredStartTime: string
    offeredEndTime: string
    offeredStartTimeLocal?: string
    offeredEndTimeLocal?: string
    status: number
    createdOn: string
    modifiedOn: any
    statusChangeOn: any
    hourlyRate: number
    submitTimestamp: any
    resubmittedOn: any
    isResubmitRequested: number
    numViews: any
    firstViewed: any
    lastViewed: any
    showJobSeekingStatus?: number
    fh_id: number
    isFavouriteHygienist: boolean
    expiresAt: string
    hygienist: OfferHygienist
    location?: Location
    job?: JobProps
    jobSeekingFeatureActive?: boolean;
    offerDetailsPaymentProtected?: boolean;
}

export interface OfferHygienist {
    firstName: string
    lastName: string
    preferredName?: string | null
    graduationYear: number
    firstLicensedYear?: number | null
    firstLicensedMonth?: number | null
    school: string
    CDHONumber: string
    starScore: number
    phone: string
    numBooked: number
    resumeUrl: any
    province: string
    country: number
    level: number
    photoUrl: string
    isReliable: number
    status: number
    /** Canonical server-provided membership authority for offer presentation. */
    membershipStatusCode?: string | null
    type: number
    completedJobs: number
    vaxStatus: number
    isLeftHanded?: number
    skills: SkillsProps[]
    about?: string
    showForHire?: number
    numRatedJobs?: number
    languages: Language[] | null
    membershipSummary?: {
        officeStatusLabel?: string | null
        reliabilityLabel?: string | null
        feedbackLabel?: string | null
        reliabilityPercentile?: number | null
        feedbackPercentile?: number | null
    } | null
}

export enum COUNTRY {
    'US' = 1,
    'CA' = 0,
}
export type Location = {
    status?: number;
    id: number,
    email: string,
    secondaryEmail?: string,
    accountingEmail?: string,
    address: string,
    city: string,
    province: string,
    hasCard: boolean,
    country: COUNTRY,
    stripeCustomerId: string,
    revenueStripeCustomerId?: string | null,
    removed: number,
    avgApptTime: string,
    primaryContact: string,
    friendlyEndorsed?: number,
    equipEndorsed?: number,
    ppeEndorsed?: boolean;
    parking: string,
    radiography: string,
    ultrasonic: string,
    charting: string,
    software: string,
    practiceName?: string,
    isCardValid?: boolean,
    revenuePaymentInfoValid?: boolean,
    postalCode: string,
    lat: number,
    lon: number,
    card?: {
        last4: string,
        type: string
    },
    isCentralBooked?: number,
    scentFreePolicy?: string | null,
}

export interface HygienistProps {
    id: number,
    firstName: string,
    lastName: string,
    preferredName?: string | null,
    about?: string,
    baseCoverLetter?: string,
    address: string,
    city: string,
    country: 0 | 1,
    school?: string,
    AvailableNow: number,
    status: number,
    type: 0 | 1 | 2 | 3, // 0 - hyg, 1 - ast, 2 - admin/manager
    Verified: 0 | 1,
    countryStr: string,
    showForHire?: number,

    InstabookActivated: 0 | 1,
    autobookTimeZone?: string | null,
    WeeklyNewsletter: 0 | 1,
    blockedUntil: number | null,
    completedJobs: number,
    createdOn: string,
    enabled: 0 | 1,
    engagedPromotionStatus: 0 | 1,
    etransferEmail: null,
    googleReviewed: 0 | 1,
    graduationYear: number,
    firstLicensedYear?: number | null
    firstLicensedMonth?: number | null,
    educationCredentialsConfirmedOn?: string | null,
    hireResumeUrl: null,
    isComplete: 0 | 1,
    isJobBoardHelpMsgSeen: 0 | 1,
    isOrtho: 0 | 1,
    isReliable: 0 | 1,
    isRestorative: 0 | 1,
    level: number | undefined,
    maxBooked: number,
    maxRate: number | null,
    numJobApplications: number,
    phone: string,
    photoUrl: string | null,
    postalCode: string,
    province: string,
    receivePostedShifts: 0 | 1,
    resumeUrl: string | undefined,
    seenPhoto: 0 | 1,
    stericenter: null,
    uploadedPhoto: 0 | 1,
    CDHONumber?: number | null,
    skills?: SkillsProps[],
    isLeftHanded?: 0 | 1,
    lat?: number,
    lon?: number,
    /** Optional announcement/photo URL fields used for home-page overlay */
    photoAnnounceUrl?: string | null,
    announceUrl?: string | null,
    isNoShiftsAvailableModalSeen: 0 | 1,
    // Admin/Reception agreement timestamp (nullable). When set, assistant can view Admin shifts
    agreedAdmin?: string | null,
    stripeCreatedTimestamp?: string | null,
}

export interface DentistProps {
    cancelledShiftsNum: number
    completedJobs: number
    country: number
    countryStr: string
    disengagedPromotionStatus: number
    engagedPromotionStatus: number
    googleReviewed: number
    id: number
    isComplete: number
    newTermsSeenOn: string
    nonPaymentReportsNum: number
    tempAmount: number
}

export interface UserAccountProps {
    country: number;
    id: number
    seenTokensInfo?: 0 | 1
    TokenAmount?: number
    email: string
    dentistId?: number
    hygienistId?: number
    dentist?: DentistProps
    hygienist?: HygienistProps
    locations?: Location[]
}

export interface ShiftProps {
    actualEnd: TimeString | null ,
    actualStart: TimeString | null,
    gapUnpaid: number,
    id: number,
    jobId: number,
    postedEnd: string,
    postedStart: string,
    /** T2-5444: optional first-patient time (`h:mm a` when present); null/omit when not set */
    firstPatientTime?: string | null,
    shiftDate: string,
    type: number,
    unpaidHours: number | null,
}

/**
 * `YYYY-MM-DD` formated date
 */
export type DateString = string & { readonly brand: unique symbol };

/**
 * `h:mm a` formated time
 */
export type TimeString = string & { readonly brand: unique symbol };

export enum JOB_STATUSES {
    POSTED = 1,
    PARTIAL = 2,
    CONFIRMED = 3,
    COMPLETED = 4,
    OFFER_EXPIRED = 5,
}

export interface JobProps {
    Autobook: number,
    Instabook: number,
    instabookTimeLeft?: number,
    dentistId: number,
    hourlyRate: number,
    bookedOn?: string,

    hygienistId: number,


    dentistRating?: number,
    dentistPrivateNotes?: string,
    shiftNotes?: string | null,

    avgRate?: number,
    distanceObj?: {
        distance: { text: string, value: number },
        duration: { text: string, value: number }
    },

    id: number,
    distance: string,

    isFavouriteDentist: number,
    isHided: boolean,

    offer_status?: number,

    isOrtho?: number,
    isRestorative?: number,

    location: Location, // LocationProps
    locationId: number,
    partialOffers: any[],
    paymentTerms: number;
    paysW2: 0 | 1 | 2,
    postedOn: string,

    ppe?: any,
    proposedRate: number,

    reposted: 0 | 1 | null,
    requiredLevel?: number,
    requiredN95?: number,
    shiftCancelled?: number,

    shifts: ShiftProps[],
    startDate: DateString,
    status: JOB_STATUSES,
    timeZone?: string,
    type: 0 | 1 | 2, // 0 - hygienist, 1 - assistant, 2 - admin/reception
    paymentMethod: number | null,
    ghr?: number,
    dentistSurveySkipCount?: number | null,
    dentistSurveySkippedAt?: string | null,

    // T2-7103/T2-7104: canonical requester membership projection returned by
    // job-detail endpoints. Raw compatibility fields (for example
    // `isReliable`) must never be used to reconstruct these decisions.
    membershipAuthorityAvailable?: boolean;
    membershipStatusCode?: string | null;
    membershipDisplayLabel?: string | null;
    membershipStatusCategory?: MembershipStatusCategory | null;
    membershipTempShiftAccess?: TempShiftAccess | null;
    membershipOverlayState?: MembershipOverlayState | null;
    
    // Admin-specific properties (T2-3952)
    // Note: the /jobs/:id/details API nests attire inside adminTasks (numeric 0/1/2);
    // the top-level `attire` below is not populated by that endpoint (T2-6509).
    adminTasks?: {
        answerPhone?: boolean;
        takePayments?: boolean;
        outboundPatientCalls?: boolean;
        hygieneCoordinating?: boolean;
        checkinPatients?: boolean;
        seatPatients?: boolean;
        stericentre?: boolean;
        floater?: boolean;
        attire?: number;
    };
    attire?: string;
    
    // GHR Bonus Promotional System (T2-4953)
    ghrBonus?: {
        rate: number,
        tier: number,
        reason: string,
        province: string,
        endDate: string,
        tier2EndDate: string,
        windowMeta: {
            province: string,
            talentType: number,
            startDate: string,
            endDate: string,
            tier1RateCents: number,
            tier2RateCents: number,
            tier2UnlockThreshold: number,
            tier2DurationDays: number,
        },
    },
    
    allowPartialOffers?: boolean,
    // T2-4286: frontend overlay flags from API (temping boost/feature)
    isBoostDisplayed?: boolean;
    isPinned?: boolean;
    // T2-4286: subtle boost indicator when job is boosted but not currently featured
    isBoosted?: boolean;
    // T2-4286: rotation ordering for featured overlay
    displayRank?: number;
    // T2-4387: Job Board featured listing flags
    isFeatureDisplayed?: boolean;
    isFeatured?: boolean;
    // T2-4133: server-provided dynamic cancellation label for cancelled/reposted shifts
    cancellationLabel?: { text: string; color: string; updatedAt?: string } | null;
    // AC-Boost: actively boosted membership regardless of display overlay
    isInRotation?: boolean;
    offerStats?: {
        offerCount: number;
        avgRate: number | null;
    };
}

export interface JobPropsWithHygienist extends JobProps {
    cancelledOffer: boolean;
    hygienist: OfferHygienist
}

export interface InvoicePayNowQuote {
    invoiceId: number;
    baseAmountCents: number;
    feeAmountCents: number;
    officeAmountCents: number;
    feeBasisPoints: number;
    feeWaived: boolean;
    currency: 'cad' | 'usd';
}

export interface InvoiceProps {
    id: number
    jobId: number
    createdOn: string
    creditPaid: number | null
    dentistMarkedPaid: number
    hourlyRate: number
    hygienistMarkedPaid: number
    paidBy?: string | null
    manualDentistId: number | null
    manualHygienistId: number | null
    nonPaymentReportedOn: string | null
    nonPaymentEscalatedOn?: string | null
    officeMarkedPaidOn?: string | null
    talentMarkedPaidOn?: string | null
    paidMethod?: string | null
    paidNote?: string | null
    officeMarkedPaid?: boolean
    notes: string | null
    paidOn: string | null
    resolved: number | null
    sent: number | null
    sentOn: string | null
    ticketLinks: string | null
    totalBillableHours: number | null
    totalHours: number
    totalInvoiceAmt: number
    totalUnpaidHours: number
    lunchHours?: number | null
    downtimeHours?: number | null
    travelAllowance: number | null
    unresolved: number | null
    paymentLifecycleId?: number
    officePaymentStatus?: 'processing' | 'action_required' | 'paid'
    talentPaymentStatus?: 'not_started' | 'processing' | 'action_required' | 'paid'
    talentPaymentMethod?: 'instant' | 'standard' | null
    payNowQuote?: InvoicePayNowQuote
}

export interface AutocompleteProps {
    buildingNumber?: string
    state: string,
    city?: string,
    postalCode?: string,
    address?: string,
    valueEqual?: boolean
}

export type PositionProps = {
    id: number;
    userId: number;
    jobTitle: string,
    companyName: string,
    city: string,
    employmentType: string,
    current: boolean,
    fromDate: dayjs.Dayjs | string,
    toDate: dayjs.Dayjs | string,
    description?: string;
}

export interface BasicHygienistScoreProps {
    cancellationScore: number,
    overallReliability: number,
    recentReliability: number,
    weightedReliability: number,
}

export interface HygienistScoreProps extends BasicHygienistScoreProps {
    cancellations: number,
    cancellations30Days: number,
    cancellations90Days: number,
    completed: number,
    completed30Days: number,
    completed90Days: number,
    newCancellationScore: number,
    newOverallScore: number,
    newRecentScore: number,
    newWeightedScore: number,
    reliabilityPercentile?: number | null,
    reliabilityLabel?: string | null,
    newReliabilityPercentile?: number | null,
    newReliabilityLabel?: string | null,
    hygienist: {
        id: number,
        firstName: string
    }
}

export interface FiltersProps {
    distanceIdx: number,
    rateIdx: number,
    selectedSort: number,
    favourite: boolean,
    date?: string,
    withDate?: boolean,
    shiftTypeIdx?: number
}

export enum JOB_SEEKING_STATUS {
    TEMPING_ONLY = 0,
    ACTIVELY_LOOKING = 1,
    OPEN_TO_OPPORTUNITIES = 2,
}
